import type { Href } from 'expo-router';
import { useRouter } from 'expo-router';
import { useCallback } from 'react';

import { useAuth } from '~/components/providers/AuthProvider';
import { useSignalR } from '~/components/providers/SignalRProvider';
import type { CommunityCode } from '~/constants/communities';
import { setSwitchingToCommunity } from '~/libs/navigation/communitySwitchStore';

const nextPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

// The overlay must always come down: if a switch await never settles (e.g.
// network gone mid-switch), drop the cover rather than leaving the app behind
// an opaque, touch-blocking screen until force-close.
const OVERLAY_FAILSAFE_MS = 15000;

/**
 * Orchestrates a community switch behind the full-screen CommunitySwitchOverlay.
 * The ordering is the point: the overlay must be on screen before anything
 * visible changes (SignalR drop, the navigation reset inside switchCommunity,
 * the theme change that follows from currentCommunity via ThemeProvider's
 * derivation), and it comes down only after the destination community's tree
 * has painted — otherwise the mid-reset teardown shows as a bare blank frame.
 */
export function useCommunitySwitch() {
  const { switchCommunity, currentCommunity } = useAuth();
  const { disconnect } = useSignalR();
  const router = useRouter();

  /**
   * @param code Community to switch to
   * @param landingHref Route to land on once switched (e.g. the default feed)
   */
  const performCommunitySwitch = useCallback(
    async (code: CommunityCode, landingHref: Href) => {
      // Re-picking the current community (directory back button, tapping its
      // own tile) is not a switch: just go back. No overlay, no reset, and
      // back() (unlike a replace to '/', which round-trips through the index
      // gate and pops tabs' child screens) returns to the app exactly as it
      // was left. The global theme never changed while browsing (directory
      // surfaces scope their branding locally), so there is nothing to undo.
      if (code === currentCommunity) {
        if (router.canGoBack()) {
          router.back();
        } else {
          router.replace(landingHref);
        }
        return;
      }

      // The target drives the overlay (its logo) and is dropped by the
      // DESTINATION: the freshly mounted (app) layout clears it once its tree
      // has painted (see AppLayout), so the cover outlives frame-count
      // guesses at this dispatch site — those fired before the heavy tab tree
      // first-painted and flashed a white frame. The timer is a failsafe for
      // a landing that never mounts; a late no-op fire after a successful
      // clear is harmless.
      setSwitchingToCommunity(code);
      setTimeout(() => setSwitchingToCommunity(null), OVERLAY_FAILSAFE_MS);
      await nextPaint();
      try {
        await disconnect();
        // Flips currentCommunity (ThemeProvider derives the new theme from
        // it) and resets the navigation tree — all behind the overlay.
        await switchCommunity(code);
        router.replace(landingHref);
      } catch (error) {
        // Nothing themed to roll back: the global theme only changes once
        // switchCommunity commits the new community.
        setSwitchingToCommunity(null);
        throw error;
      }
    },
    [disconnect, switchCommunity, router, currentCommunity]
  );

  return { performCommunitySwitch };
}
